import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { Home } from '@/pages/Home';
import { AreaPage } from '@/pages/AreaPage';
import { SearchPage } from '@/pages/SearchPage';
import { RecentsPage } from '@/pages/RecentsPage';
import { NotFound } from '@/pages/NotFound';
import { UserProvider } from '@/contexts/UserContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Login } from '@/pages/auth/Login';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { ChangePassword } from '@/pages/auth/ChangePassword';
import { MfaSetup } from '@/pages/auth/MfaSetup';
import { MfaChallenge } from '@/pages/auth/MfaChallenge';
import AdminLayout from '@/pages/admin/AdminLayout';

const AdminOverview  = lazy(() => import('@/pages/admin/AdminOverview'))
const AreaTree       = lazy(() => import('@/pages/admin/AreaTree'))
const AreaEdit       = lazy(() => import('@/pages/admin/AreaEdit'))
const DocumentTypes  = lazy(() => import('@/pages/admin/DocumentTypes'))
const FileManagement = lazy(() => import('@/pages/admin/FileManagement'))
const FileUpload     = lazy(() => import('@/pages/admin/FileUpload'))
const FileEdit       = lazy(() => import('@/pages/admin/FileEdit'))
const ProfilesPage   = lazy(() => import('@/pages/admin/ProfilesPage'))
const ProfileEditPage = lazy(() => import('@/pages/admin/ProfileEditPage'))
const UsersPage      = lazy(() => import('@/pages/admin/UsersPage'))
const UserEditPage   = lazy(() => import('@/pages/admin/UserEditPage'))
const AdminSettings  = lazy(() => import('@/pages/admin/AdminSettings'))
const AuditLogsPage  = lazy(() => import('@/pages/admin/AuditLogsPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function AppShell({ children }: { children: ReactNode }) {
  let content: ReactNode = children;
  content = <UserProvider>{content}</UserProvider>;
  return <>{content}</>;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppShell>
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* M2: /register e /confirm-email removidos. Usuários via convite do admin. */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mfa/challenge" element={<MfaChallenge />} />
              {/* /mfa/setup é pre-sessão: o user chega aqui via convite ou via
                  login com state=mfa-setup-required (sem cookie ainda; só um
                  pendingToken no location.state). Mantê-lo dentro do
                  ProtectedRoute causava redirect para /login → loop. */}
              <Route path="/mfa/setup" element={<MfaSetup />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/areas/:slug" element={<AreaPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/recents" element={<RecentsPage />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                </Route>
              </Route>
              <Route element={<ProtectedRoute />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Suspense><AdminOverview /></Suspense>} />
                  <Route path="areas" element={<Suspense><AreaTree /></Suspense>} />
                  <Route path="areas/:areaId/edit" element={<Suspense><AreaEdit /></Suspense>} />
                  <Route path="types" element={<Suspense><DocumentTypes /></Suspense>} />
                  <Route path="files" element={<Suspense><FileManagement /></Suspense>} />
                  <Route path="files/upload" element={<Suspense><FileUpload /></Suspense>} />
                  <Route path="files/:fileId/edit" element={<Suspense><FileEdit /></Suspense>} />
                  <Route path="profiles" element={<Suspense><ProfilesPage /></Suspense>} />
                  <Route path="profiles/:id" element={<Suspense><ProfileEditPage /></Suspense>} />
                  <Route path="users" element={<Suspense><UsersPage /></Suspense>} />
                  <Route path="users/:id" element={<Suspense><UserEditPage /></Suspense>} />
                  <Route path="settings" element={<Suspense><AdminSettings /></Suspense>} />
                  <Route path="audit" element={<Suspense><AuditLogsPage /></Suspense>} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
        </BrowserRouter>
      </QueryClientProvider>
      <Toaster richColors position="top-right" />
    </ErrorBoundary>
  );
}
